import { Box } from "@mui/material";
import { useTheme } from "@mui/material";

import { tokens } from "../../../../../theme";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
import { useGetPalletspotsQuery } from "../../../../../newapi/warehouse/palletSpotSlice";
import ShDataGrid2 from "../../../../theme/datagrid2/ShDataGrid2";
import Text from "../../../../theme/text/Text";
import { generateButtonColors, getContrastText } from "../../../../../environments/global/branding/brandConfig";
import { hslToHex } from "../../../../../utils/colorConverters";
import { generateHueFromTitle } from "../../../../theme/TabView3/IconColorGenerator";
import SHQuickAction from "../../../../theme/Modal/SHQuickAction";

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    

};

const GridActions = (props) => {
    return <></>;
}

// One row per location type: a coloured label holding the type name, with the
// bundled and location counts as filled pills (with contrasting text) inside it.
const PerLocationTypeRow = ({ item, mode, fullWidth }) => {
    const { t } = useTranslation();
    const btn = generateButtonColors(hslToHex(generateHueFromTitle(item.type ?? "-"), 80, 45));

    return (
        <Box
            sx={{
                height: 32,
                pl:1.5,
                pr:0.5,
                display: "flex",
                alignItems: "center",
                width: fullWidth ? "auto" : "fit-content",
                borderRadius: 4,
                border: `1px solid ${mode === "dark" ? btn.darkText : btn.light}`,
                background: mode === "dark" ? `rgba(${btn.darkTextRgb.join(",")}, 0.16)` : `rgba(${btn.lightRgb.join(",")}, 0.12)`,
                color: mode === "dark" ? btn.darkText : btn.light,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                flexShrink:0,
                fontWeight:600,
            }}
        >
            <Box sx={{ mr: "auto" }}>{item.type}</Box>
            {item.bundled !== null && item.bundled !== undefined &&
                <Box
                    sx={{
                        ml:2,
                        px:1,
                        gap:1,
                        borderRadius:5,
                        fontWeight:400,
                        whiteSpace:"nowrap",
                        display: "flex",
                        alignItems: "center",
                        background: mode === "dark" ? btn.darkText : btn.light,
                        color: mode === "dark" ? btn.contrastDark : btn.contrastLight,
                    }}
                >
                    <b>{item.bundled}</b> {t("bundles")}
                </Box>
            }
            <Box
                sx={{
                    ml:1,
                    px:1,
                    gap:1,
                    borderRadius:5,
                    fontWeight:400,
                    whiteSpace:"nowrap",
                    display: "flex",
                    alignItems: "center",
                    background: mode === "dark" ? btn.darkText : btn.light,
                    color: mode === "dark" ? btn.contrastDark : btn.contrastLight,
                }}
            >
                <b>{item.locations}</b> {t("locations")}
            </Box>
        </Box>
    );
}

const Palletspots = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    const [data, setData] = useState([]);
    const [config, setConfig] = useState({relationId: props.relationId});

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions/>,
            gridOptions:gridOptions,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[]);

    

    const columns = [
        {
            field: "date", 
            headerName: t('date'),
            flex:1, 
            // cellClassName: "sku-cell", 
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                let date = new Date(params.row.date);
              
                return (
                    <Text>
                        {date.toLocaleDateString(i18n.language)}
                    </Text>
                );
            }
        },
        {
            field: "totalLocations", 
            headerName: t('total_physical_locations'),
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.totalLocations
            }
        },
        {
            field: "invoicablePalletspots", 
            headerName: t('total_invoicable_locations'),
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.invoicablePalletspots
            }
        },
        {
            field: "per_location_type", 
            headerName: t('per_location_type'),
            flex:5,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <SHQuickAction
                        disableScrollLock={true}
                        component={
                            <Box 
                                sx={{display:"flex",flexDirection:"column",gap:1,}}
                                aria-haspopup={false}
                            >
                                {params.row.perLocationType?.map((item, index) => (
                                    <PerLocationTypeRow key={"plt2"+index} item={item} mode={theme.palette.mode} fullWidth />
                                ))}
                            </Box>
                        }
                    >
                        {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                            <Box
                                sx={{display:"flex", alignItems:"center", gap:1}}
                                aria-owns={anchorEl ? 'simple-popover' : undefined}
                                aria-haspopup={false}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onTouchStart={onTouchStart}
                                sx={{
                                    width:1,
                                    display:'flex',
                                    gap:1,
                                }}
                            >
                                {params.row.perLocationType?.slice(0, 1).map((item, index) => (
                                    <PerLocationTypeRow key={"plt"+index} item={item} mode={theme.palette.mode} />
                                ))}
                                {params.row.perLocationType?.length > 1 &&
                                    <Box
                                        sx={{
                                            height:32,
                                            px:2,
                                            flexShrink:0,
                                            display:"flex",
                                            alignItems:"center",
                                            gap:1,
                                            borderRadius:4,
                                            color:colors.txt["secondary"],
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                        }}
                                    >
                                        <Text bold>+{params.row.perLocationType.length - 1}</Text> <Text light>{t("more")}</Text>
                                    </Box>
                                }
                            </Box>
                        )}
                    </SHQuickAction>
                )
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return <></>
            }
        },
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}
            sx={{
                // "& .sku-cell" : {
                //     paddingLeft:0,
                // },
                // "& [aria-label='SKU']" : {
                //     paddingLeft:0,
                // },
            }}
        >
            {/* <GridActions></GridActions> */}
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetPalletspotsQuery}
                config={config}
                gridActions={<GridActions></GridActions>} 
                title={t('palletspots')}
                gridOptions={gridOptions} 
                isSidepanelGrid 
                columns={columns} 
                rows={data}>
            </ShDataGrid2>
        </Box>
    );
}

export default Palletspots;